import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import type { RobotClient, SavDossier } from "@shared/schema";

export function useRobotsClients(clientId: string | null) {
  return useQuery<RobotClient[]>({
    queryKey: ["/api/clients", clientId, "robots"],
    queryFn: async () => {
      const res = await authFetch(`/api/clients/${clientId}/robots`);
      if (!res.ok) throw new Error("Failed to fetch client robots");
      return res.json();
    },
    enabled: !!clientId,
  });
}

export function useRobotClient(id: string | null) {
  return useQuery<RobotClient>({
    queryKey: ["/api/robots-clients", id],
    queryFn: async () => {
      const res = await authFetch(`/api/robots-clients/${id}`);
      if (!res.ok) throw new Error("Failed to fetch robot");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateRobotClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, ...data }: any) => {
      const res = await authFetch(`/api/clients/${clientId}/robots`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create robot");
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/clients", variables.clientId, "robots"] });
    },
  });
}

export function useUpdateRobotClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, clientId, ...data }: any) => {
      const res = await authFetch(`/api/robots-clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update robot");
      const result = await res.json();
      return { ...result, _clientId: clientId };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["/api/clients", data._clientId, "robots"] });
      qc.invalidateQueries({ queryKey: ["/api/robots-clients"] });
    },
  });
}

export function useDeleteRobotClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, clientId }: { id: string; clientId: string }) => {
      const res = await authFetch(`/api/robots-clients/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete robot");
      return { clientId };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/clients", variables.clientId, "robots"] });
      qc.invalidateQueries({ queryKey: ["/api/robots-clients"] });
    },
  });
}

export function useRobotsReseller(resellerId: string | null) {
  return useQuery<RobotClient[]>({
    queryKey: ["/api/resellers", resellerId, "robots"],
    queryFn: async () => {
      const res = await authFetch(`/api/resellers/${resellerId}/robots`);
      if (!res.ok) throw new Error("Failed to fetch reseller robots");
      return res.json();
    },
    enabled: !!resellerId,
  });
}

export function useCreateRobotReseller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ resellerId, ...data }: any) => {
      const res = await authFetch(`/api/resellers/${resellerId}/robots`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to create robot");
      }
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/resellers", variables.resellerId, "robots"] });
    },
  });
}

export function useUpdateRobotReseller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, resellerId, ...data }: any) => {
      const res = await authFetch(`/api/reseller-robots/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update robot");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/resellers", variables.resellerId, "robots"] });
    },
  });
}

export function useDeleteRobotReseller() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, resellerId }: { id: string; resellerId: string }) => {
      const res = await authFetch(`/api/reseller-robots/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete robot");
      return { resellerId };
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["/api/resellers", variables.resellerId, "robots"] });
    },
  });
}

export function useRobotModelImages() {
  return useQuery<Record<string, string>>({
    queryKey: ["/api/robot-model-images"],
    queryFn: async () => {
      const res = await authFetch("/api/robot-model-images");
      if (!res.ok) throw new Error("Failed to fetch model images");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useRobotsClientsOrphelins() {
  return useQuery<RobotClient[]>({
    queryKey: ["/api/robots-clients", "orphelins"],
    queryFn: async () => {
      const res = await authFetch("/api/robots-clients?a_completer=true");
      if (!res.ok) throw new Error("Failed to fetch orphelin robots");
      return res.json();
    },
    staleTime: 30 * 1000,
  });
}

export function useRobotClientSavHistory(robotClientId: string | null) {
  return useQuery<SavDossier[]>({
    queryKey: ["/api/robots-clients", robotClientId, "sav"],
    queryFn: async () => {
      const res = await authFetch(`/api/robots-clients/${robotClientId}/sav`);
      if (!res.ok) throw new Error("Failed to fetch SAV history");
      return res.json();
    },
    enabled: !!robotClientId,
  });
}
